import React, { useState } from "react"
import { Box, Menu, Tooltip } from "@mui/material"
import { COLOR_OPTIONS } from "../utils/bluetooth"

const dotSx = (rgb, selected) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  p: 0,
  flexShrink: 0,
  cursor: "pointer",
  border: selected ? "2px solid" : "1px solid",
  borderColor: selected ? "text.primary" : "divider",
  bgcolor: `rgb(${rgb.join(",")})`,
})

// The "none" swatch: an empty dot with a red diagonal slash
const noneSx = (selected) => ({
  ...dotSx([0, 0, 0], selected),
  bgcolor: "transparent",
  backgroundImage:
    "linear-gradient(to top right, transparent 44%, #e53935 44%, #e53935 56%, transparent 56%)",
})

/**
 * A wall color as a small swatch dot; tapping it opens the full 8x8 grid
 * of colors the wall can show. `value` / `onChange` are wire color bytes.
 *
 * With `noneLabel`, the grid also offers a "none" swatch (a slashed empty
 * dot) whose value is null.
 */
export default function ColorDotPicker({ label, value, onChange, noneLabel }) {
  const [anchorEl, setAnchorEl] = useState(null)
  const current = COLOR_OPTIONS.find((option) => option.byte === value)
  const isNone = noneLabel != null && value == null
  const currentName = isNone ? noneLabel : current?.name ?? value
  const pick = (byte) => {
    onChange(byte)
    setAnchorEl(null)
  }
  return (
    <>
      <Tooltip title={`${label}: ${currentName}`}>
        <Box
          component="button"
          aria-label={label}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={isNone ? noneSx(false) : dotSx(current?.rgb ?? [0, 0, 0], false)}
        />
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(8, 20px)",
            gap: 0.5,
            px: 1,
            py: 0.5,
          }}
        >
          {COLOR_OPTIONS.map(({ byte, name, rgb }) => (
            <Box
              key={byte}
              component="button"
              role="menuitemradio"
              aria-checked={byte === value}
              title={name}
              onClick={() => pick(byte)}
              sx={dotSx(rgb, byte === value)}
            />
          ))}
          {noneLabel != null && (
            <Box
              component="button"
              role="menuitemradio"
              aria-checked={isNone}
              title={noneLabel}
              onClick={() => pick(null)}
              sx={noneSx(isNone)}
            />
          )}
        </Box>
      </Menu>
    </>
  )
}
